import moment from 'moment'
import { getDirectDropboxImageUrl } from '~/utils/dropboxImageUrl'

export const useUtils = () => {
  const { t } = useI18n()

  type TimeStampType = Date | string

  enum TimeStampFormat {
    DayMonthYear = 'DayMonthYear',
    DayMonthYearTime = 'DayMonthYearTime',
    DateOnly = 'DateOnly',
    DateTime = 'DateTime',
    TimeOnly = 'TimeOnly',
    FullDateTime = 'FullDateTime',
    MonthAndYear = 'MonthAndYear',
    Year = 'Year',
    FullDateTimeTwelveHour = 'FullDateTimeTwelveHour',
    DateTimeTwelveHour = 'DateTimeTwelveHour',
    FullDateTimeWithTwelveHour = 'FullDateTimeWithTwelveHour',
  }

  const DATE_FORMATS = {
    [TimeStampFormat.DateOnly]: t('dateFormats.DateOnly'),
    [TimeStampFormat.DateTime]: t('dateFormats.DateTime'),
    [TimeStampFormat.TimeOnly]: t('dateFormats.TimeOnly'),
    [TimeStampFormat.FullDateTime]: t('dateFormats.FullDateTime'),
    [TimeStampFormat.DayMonthYear]: t('dateFormats.DayMonthYear'),
    [TimeStampFormat.DayMonthYearTime]: t('dateFormats.DayMonthYearTime'),
    [TimeStampFormat.MonthAndYear]: t('dateFormats.MonthAndYear'),
    [TimeStampFormat.Year]: t('dateFormats.Year'),
    [TimeStampFormat.FullDateTimeTwelveHour]: t('dateFormats.FullDateTimeTwelveHour'),
    [TimeStampFormat.FullDateTimeWithTwelveHour]: t('dateFormats.FullDateTimeWithTwelveHour'),
    [TimeStampFormat.DateTimeTwelveHour]: t('dateFormats.DateTimeTwelveHour'),
  }

  type DollarFormat = {
    original: number
    formatted: string
  }

  const formatCurrency = (
    amount: number,
    locale: string,
    currency: string,
  ): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(amount)
  }

  const numberRollFormatToCurrency = (value: number): { prefix: string, original: number, formatted: string, suffix: string, decimalPlaces: number } | { original: number, formatted: string } => {
    try {
      const prefix = "$";
      let suffix = "";
      let formatted

      if (value >= 1000000) {
        formatted = (value / 1000000).toFixed(2)
        suffix = 'M'
      }
      else if (value >= 1000) {
        formatted = (value / 1000).toFixed(2)
        suffix = 'K'
      }
      else {
        formatted = value.toFixed(2)
      }

      return {
        prefix,
        original: value,
        formatted,
        suffix,
        decimalPlaces: suffix == 'M' ? 2 : 1
      };
    } catch (e) {
      return {
        prefix: '$',
        original: value,
        formatted: "-",
        suffix: '',
        decimalPlaces: 0
      };
    }
  };

  const numberRollFormatToPercentage = (value: number): { prefix: string, original: number, formatted: string, suffix: string, decimalPlaces: number } => {
    try {
      const prefix = "";
      const suffix = "%";
      const formatted = value.toFixed(0)
      return {
        prefix,
        original: value,
        formatted,
        suffix,
        decimalPlaces: 0
      };
    } catch (e) {
      return {
        prefix: '',
        original: value,
        formatted: "-",
        suffix: '',
        decimalPlaces: 0
      };
    }
  };

  const numberRollConvertToUnits = (units: number, suffixText: string) => {
    const prefix = "";
    let suffix = suffixText;
    return {
      prefix,
      original: units,
      formatted: units,
      suffix,
      decimalPlaces: 0
    }
  }

  const formatToDollars = (amount: number): DollarFormat => {
    if (isFinite(amount)) {
      return {
        original: amount,
        formatted: formatCurrency(amount, 'en-AU', 'AUD'),
      }
    }
    else {
      return {
        original: '-',
        formatted: '-',
      }
    }
  }

  const formatToDollarsWithoutCents = (
    amount: number,
    currency?: any,
  ): DollarFormat => {
    if (currency === 'AUD' || !currency) {
      if (isFinite(amount)) {
        return {
          original: amount,
          formatted: formatCurrency(Math.floor(amount), 'en-AU', 'AUD').replace(
            /\.\d+$/,
            '',
          ),
        }
      }
      else {
        return {
          original: '-',
          formatted: '-',
        }
      }
    }
    else {
      console.log('Currency:', currency)
      const newAmount = amount * (currency?.rate || 1)
      if (isFinite(newAmount)) {
        return {
          original: newAmount,
          formatted: `${currency?.symbol}${Math.floor(
            newAmount,
          ).toLocaleString()}`,
        }
      }
      else {
        return {
          original: '-',
          formatted: '-',
        }
      }
    }
  }

  const formatToDollarsWithoutDecimals = (amount: any): DollarFormat => {
    if (isFinite(amount)) {
      return {
        original: amount,
        formatted:
          amount == 0
            ? formatCurrency(amount, 'en-AU', 'AUD')
            : formatCurrency(amount, 'en-AU', 'AUD').replace(/\.\d+$/, ''),
      }
    }
    else {
      return {
        original: '-',
        formatted: '-',
      }
    }
  }

  const calculateDifferenceAmount = (
    startingAmount: number,
    endingAmount: number,
  ): DollarFormat => {
    const difference = endingAmount - startingAmount
    return {
      original: difference,
      formatted: formatCurrency(difference, 'en-AU', 'AUD'),
    }
  }

  const formatUnixTimeStamp = (
    timestamp: number,
    format: TimeStampFormat,
  ): string => {
    const date = new Date(timestamp * 1000)
    return moment(date).format(DATE_FORMATS[format])
  }

  const formatToCurrency = (value: number) => {
    let formatted
    if (value >= 1000) {
      formatted = `$${(value / 1000).toFixed(1)}K`
    }
    else {
      formatted = `$${value.toFixed(2)}`
    }
    return {
      original: value,
      formatted,
    }
  }

  const formatSupabaseTimeStamp = (
    timestamp: TimeStampType,
    format: TimeStampFormat,
  ): { original: TimeStampType, formatted: string, sorting: string } => {
    try {
      const date = new Date(timestamp)
      const formattedDate = moment(date).format(DATE_FORMATS[format])
      const sortingDate = moment(date).format('YYYYMMDDHHmmss')

      return {
        original: timestamp,
        formatted: formattedDate,
        sorting: sortingDate,
      }
    }
    catch (err) {
      console.error(err)
      return {
        original: timestamp,
        formatted: '',
        sorting: '',
      }
    }
  }

  const useAgo = (timestamp: TimeStampType): string => {
    try {
      const date = new Date(timestamp)
      return moment(date).fromNow()
    }
    catch (err) {
      console.error(err)
      return ''
    }
  }

  const compareDates = (date1: TimeStampType, date2: TimeStampType): number => {
    const moment1 = moment(new Date(date1))
    const moment2 = moment(new Date(date2))
    return moment1.diff(moment2)
  }

  const isSameDay = (date1: TimeStampType, date2: TimeStampType): boolean => {
    const moment1 = moment(new Date(date1))
    const moment2 = moment(new Date(date2))
    return moment1.isSame(moment2, 'day')
  }

  const getCurrentDateTime = (
    format: TimeStampFormat,
  ): { original: TimeStampType, formatted: string, sorting: string } => {
    return formatSupabaseTimeStamp(new Date().toISOString(), format)
  }

  const compareNumericValue = (currentValue: number, previousValue: number) => {
    if (previousValue === 0 || currentValue === 0) {
      return null
    }

    const difference = currentValue - previousValue
    const percentageChange = (difference / previousValue) * 100
    const isPositive = difference > 0

    return {
      value: parseFloat(percentageChange.toFixed(2)),
      positive: isPositive,
    }
  }

  const sortByNumberName = (values: any[] = [], key: string = ''): any[] => {
    if (!Array.isArray(values) || !key) return []
    return [...values].sort((a, b) => {
      const valueA = (a && typeof a === 'object' && a[key] != null) ? String(a[key]) : ''
      const valueB = (b && typeof b === 'object' && b[key] != null) ? String(b[key]) : ''

      const nameA = valueA.toLowerCase()
      const nameB = valueB.toLowerCase()

      const startsWithNumA = /^\d/.test(nameA)
      const startsWithNumB = /^\d/.test(nameB)

      if (startsWithNumA && startsWithNumB) {
        const numA = parseFloat((nameA.match(/^\d+(?:\.\d+)?/) || ['0'])[0])
        const numB = parseFloat((nameB.match(/^\d+(?:\.\d+)?/) || ['0'])[0])

        if (numA !== numB) {
          return numA - numB
        }
        return nameA.localeCompare(nameB)
      }

      if (startsWithNumA) {
        return -1
      }

      if (startsWithNumB) {
        return 1
      }

      return nameA.localeCompare(nameB)
    })
  }

  const isPastDate = (date: TimeStampType): boolean => {
    const now = new Date()
    const dateObj = new Date(date)
    return dateObj.getTime() < now.getTime()
  }

  const isFutureDate = (date: TimeStampType): boolean => {
    const now = new Date()
    const dateObj = new Date(date)
    return dateObj.getTime() > now.getTime()
  }

  const formatDate = (date: any, type: TimeStampFormat) => {
    return moment(date).format(DATE_FORMATS[type])
  }

  const formatDateUTC = (date: any, type: TimeStampFormat) => {
    return moment.utc(date).format(DATE_FORMATS[type])
  }

  const roundDecimalPlaces = (value: number, decimalPlaces: number) => {
    try {
      if (isNaN(value)) {
        return 0
      }
      return parseFloat(value.toFixed(decimalPlaces))
    }
    catch (err) {
      console.error(err)
      return 0
    }
  }

  //  check if today is the same day as the supabase timestamp
  const isToday = (timestamp: TimeStampType): boolean => {
    const today = new Date().toISOString()
    const isToady = isSameDay(today, timestamp)
    return isToady
  }

  const handlePrismaError = (errorCode: string, metaData: { target: string }) => {
    let message = 'An error occurred'
    let code = errorCode
    const target = metaData?.target || 'record'
    switch (errorCode) {
      case 'P2002':
        message = `This ${target} already exists`
        break
      case 'P2025':
        message = `This ${target} was not found`
        break
      default:
        message = 'An unexpected error occurred'
        code = 'UNKNOWN_ERROR'
    }

    return { message, code }
  }

  const extractAPIResponseError = (e) => {
    if (e?.data) {
      return {
        errorCode: e.data.data?.code ? e.data.data?.code : 500,
        message: e.data.data?.message ?? e.statusMessage ?? e.message ?? 'Request failed.',
        raw: e,
      }
    }
  }

  const sendSMS = async (smsData: {
    to: string;
    body: string;
    contactId?: string;
    businessId?: string;
  }) => {
    try {
      const response = await $fetch('/api/v1/utils/send-sms', {
        method: 'POST',
        body: smsData,
      });
      return { data: response, error: null };
    } catch (error) {
      console.error('Error sending sms:', error);
      return { data: null, error };
    }
  }

  const validateEmail = async (email: string, workSpaceId?: string | null) => {
    try {
      const response = await $fetch('/api/v1/utils/validate-email', {
        method: 'POST',
        body: { email, workSpaceId },
      });

      return response;
    } catch (error) {
      throw error;
    }
  };

  const isValidUrl = (url: string): boolean => {
    if (!url) return false
    try {
      new URL(url)
      return true
    }
    catch {
      return false
    }
  }

  const getMonthName = (month: number): string => {
    return new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' })
  }

  const getDateOnLastDayOfMonth = (month: number, year: number) => {
    const lastDayDate = new Date(year, month, 0)
    return formatDate(lastDayDate, TimeStampFormat.DateOnly)
  }

  return {
    sendSMS,
    extractAPIResponseError,
    handlePrismaError,
    formatSupabaseTimeStamp,
    compareDates,
    isToday,
    getCurrentDateTime,
    sortByNumberName,
    isPastDate,
    isFutureDate,
    TimeStampFormat,
    formatUnixTimeStamp,
    formatToDollars,
    formatToDollarsWithoutCents,
    formatToDollarsWithoutDecimals,
    calculateDifferenceAmount,
    formatToCurrency,
    compareNumericValue,
    roundDecimalPlaces,
    numberRollFormatToCurrency,
    numberRollFormatToPercentage,
    numberRollConvertToUnits,
    formatDate,
    formatDateUTC,
    useAgo,
    getDirectDropboxImageUrl,
    validateEmail,
    isValidUrl,
    getMonthName,
    getDateOnLastDayOfMonth
  }
}
